import { Center, Tag } from '@chakra-ui/react'
import { ReportInfo } from '../types/ReportTypes'
import {
  formatReportDate,
  getCurrentMonthYearDisplay,
} from '../utils/reportUtils'

interface ReportTagProps {
  /** User ID */
  userId: string
  /** Reports information */
  reports: Record<string, ReportInfo>
}

/**
 * Displays a color-coded tag showing report status:
 * - Green: Report completed (allStepsCompleted)
 * - Orange: Report started but not completed
 * - Red: Never completed a report
 */
const ReportTag = ({ userId, reports = {} }: ReportTagProps) => {
  const currentMonthYearDisplay = getCurrentMonthYearDisplay()
  const userReport = reports[userId]

  // User has a current month report
  if (userReport?.currentMonth) {
    // If the current-month report has been completed (allStepsCompleted), show green
    if (userReport.currentMonthAllStepsCompleted) {
      return (
        <Tag size='md' bg='green.500' color='white' borderRadius='full'>
          <Center>{currentMonthYearDisplay}</Center>
        </Tag>
      )
    }

    // Current month exists but not completed -> show orange to indicate "started"
    return (
      <Tag size='md' bg='orange.400' color='white' borderRadius='full'>
        <Center>{currentMonthYearDisplay}</Center>
      </Tag>
    )
  }

  // User has reports but not for current month
  if (userReport?.hasReports && userReport?.mostRecentReport) {
    const mostRecentReport = userReport.mostRecentReport
    const bg = userReport.mostRecentReportCompleted ? 'green.500' : 'orange.400'

    return (
      <Tag size='md' bg={bg} color='white' borderRadius='full'>
        <Center>{formatReportDate(mostRecentReport)}</Center>
      </Tag>
    )
  }

  // User has never completed a report
  return (
    <Tag size='md' bg='red.500' color='white' borderRadius='full'>
      <Center>Never completed</Center>
    </Tag>
  )
}

export default ReportTag
